// src/context/AuthContext.jsx
import { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = async () => {
    setLoading(true);
    try {
      const res = await api.get('/me');
      if (res.data.authenticated && res.data.user) {
        setUser(res.data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Ошибка проверки сессии:', err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // При инициализации проверяем сессию
    checkAuth();
    
    // Добавляем слушатель событий для мульти-tab синхронизации
    const handleStorageChange = (e) => {
      if (e.key === 'auth_sync') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = async (username, password) => {
    try {
      const res = await api.post('/login', { username, password });
      if (res.status === 200 && res.data.success) {
        await checkAuth();
        // Синхронизируем состояние между вкладками
        window.dispatchEvent(new Event('auth_sync'));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Ошибка входа:', err);
      // Проверяем, не вернулся ли 429 (блокировка)
      if (err.response?.status === 429) {
        throw new Error(err.response.data.error || 'Слишком много попыток входа. Попробуйте позже.');
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post('/logout');
    } catch (err) {
      console.error('Ошибка выхода:', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      // Синхронизируем состояние между вкладками
      window.dispatchEvent(new Event('auth_sync'));
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
